/**
 * A strategic objective used as a criterion (migration 1066, owner ruling 2026-11-03).
 *
 * A track may choose up to three of its workspace's strategic objectives as criteria:
 * "Advances <objective>", scored item by item. The criterion is a workspace attribute
 * that points at the objective (`attributes.objective_id`), keyed
 * `objective_<id without dashes>`, named after the objective. Its one scale,
 * `objective-strength`, is the strength of the item's link to the objective: None,
 * Indirect, Secondary, Primary. The item's official value on it IS that link
 * (`objective_priority_mappings`); the database keeps the two equal both ways.
 *
 * A session scores the objective on the track's shared range, like any criterion.
 * Two places cross between that range and the four words, both here:
 * - S4 starts a grid from the record: a word becomes a number on the session's range
 *   (`objectiveStrengthOnRange`).
 * - An approver makes a session's number the record: the number becomes a word
 *   (`objectiveStrengthFromRange`).
 *
 * An objective becomes a criterion on a track the way any criterion does: a track row
 * keyed `objectiveCriterionKey(id)` (createCycleConfig). The database checks the
 * objective is the workspace's, holds the three-per-track limit, accepts its range and
 * makes its attribute; the same insert rule and lock as every criterion apply.
 */

export { OBJECTIVE_CRITERION_KEY_PREFIX, objectiveCriterionKey, isObjectiveCriterionKey } from '../../supabase/functions/_shared/objectiveCriterionKey.ts';
import { isObjectiveCriterionKey } from '../../supabase/functions/_shared/objectiveCriterionKey.ts';
export const OBJECTIVE_STRENGTH_SCALE_ID = 'objective-strength';
export const MAX_OBJECTIVES_PER_TRACK = 3;

/** The scale's words, weakest first; the index is the strength (0–3). */
export const OBJECTIVE_STRENGTH_LABELS = ['None', 'Indirect', 'Secondary', 'Primary'] as const;
export type ObjectiveStrengthLabel = (typeof OBJECTIVE_STRENGTH_LABELS)[number];

export interface ScoringRange {
  min: number;
  max: number;
}

/** True for an attribute that stands for a strategic objective. */
export function isObjectiveAttribute(attr: { key?: string; objectiveId?: string | null } | null | undefined): boolean {
  return !!attr && (!!attr.objectiveId || isObjectiveCriterionKey(attr.key));
}

/** Attributes for a list of attributes: objectives are chosen as objectives, not listed beside the rest. */
export function withoutObjectiveAttributes<T extends { key?: string; objectiveId?: string | null }>(list: readonly T[]): T[] {
  return list.filter((a) => !isObjectiveAttribute(a));
}

/** A value on the criterion (a word of the scale, or a number 0–3) as a strength 0–3; null when it is neither. */
export function objectiveStrengthOf(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? clampStrength(value) : null;
  const text = String(value).trim();
  const byWord = OBJECTIVE_STRENGTH_LABELS.findIndex((l) => l.toLowerCase() === text.toLowerCase());
  if (byWord >= 0) return byWord;
  const n = Number(text);
  return text !== '' && Number.isFinite(n) ? clampStrength(n) : null;
}

function clampStrength(n: number): number {
  return Math.min(3, Math.max(0, Math.round(n)));
}

/**
 * A strength as a number on a session's range: None is the bottom, Primary the top,
 * the two between spaced evenly, rounded to a whole point.
 */
export function objectiveStrengthOnRange(value: unknown, range: ScoringRange): number | null {
  const strength = objectiveStrengthOf(value);
  if (strength == null || !(range.max > range.min)) return null;
  return Math.round(range.min + (strength / 3) * (range.max - range.min));
}

/**
 * A number on a session's range as the scale's word: the nearest of the four points
 * `objectiveStrengthOnRange` places, so a word put on a range reads back as itself.
 */
export function objectiveStrengthFromRange(value: number, range: ScoringRange): ObjectiveStrengthLabel | null {
  if (!Number.isFinite(value) || !(range.max > range.min)) return null;
  const share = (Math.min(range.max, Math.max(range.min, value)) - range.min) / (range.max - range.min);
  return OBJECTIVE_STRENGTH_LABELS[Math.round(share * 3)];
}
